import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components/native';

import { Feather } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';

import ActivityIndicatorScreen from '../../../../components/ActivityIndicatorScreen';
import ImageGalleryModal from '../../../../components/ImageGalleryModal';
import Spacer from '../../../../components/Spacer';
import { B } from '../../../../components/Typography';
import { useStudyContext } from '../../../../context/StudyContext';
import { useUserContext } from '../../../../context/UserContext';
import environment from '../../../../environments';
import { useGetStudyParticipantFiles } from '../../../../hooks/study/useGetStudyParticipantFiles';
import { StudyParticipantImage } from '../../../../models/StudyParticipantImage';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import { log } from '../../../../tools/LogHelper';

const StyledCardView = styled.TouchableOpacity`
  flex-direction: row;
  background: #e7ffde;
  border-radius: 24px;
  shadow-color: #111111;
  shadow-offset: 0 16px;
  shadow-opacity: 0.1;
  shadow-radius: 48px;
  width: 100%;
  padding: 16px 22px 16px 16px;
  min-height: 80px;
  align-items: center;
`;

const StyledH5 = styled(B)`
  /* padding: 0 24px; */
`;

const StyledImageStackView = styled.View`
  position: relative;
  flex: 1 1 auto;
`;

const StyledTouchableOpacity = styled.View`
  position: absolute;
  /* margin: 4px; */
`;

const StyledImage = styled.Image`
  width: 32px;
  height: 32px;
  border-radius: 8px;
`;

const StyledAcessoryContainerView = styled.View`
  flex: 1 1 auto;
  align-items: flex-end;
`;

interface ImageGalleryCardProps {
  title?: string;
}

const ImageGalleryCard: React.FC<
  ScreenComponentProps<ImageGalleryCardProps>
> = ({ title = 'Photos' }) => {
  const [openImageGallery, setOpenImageGallery] = useState(false);
  const { studyParticipantId } = useStudyContext();
  const { accessToken } = useUserContext({ ignoreUndefined: true }) || {};
  const {
    studyParticipantFiles,
    loadingStudyParticipantFiles,
    getStudyParticipantFiles
  } = useGetStudyParticipantFiles(studyParticipantId || 0);
  useFocusEffect(
    useCallback(() => {
      log('refresh ImageGalleryCard');
      if (studyParticipantId) getStudyParticipantFiles();
    }, [studyParticipantId, getStudyParticipantFiles])
  );

  // get only images from participant uploaded files and use image proxy
  // endpoint to get small and large image sizes to display to prevent
  // downloading large images.
  const images = useMemo(() => {
    const images: StudyParticipantImage[] = [];
    if (!studyParticipantFiles) return images;
    const endpoint = `${environment.apiBaseUrl}files/image?token=${accessToken}`;
    for (const file of studyParticipantFiles) {
      if (!file.url) continue;
      const url = new URL(file.url);
      const queryParams = new URLSearchParams(url.search);
      const s3Key = queryParams.get('key');
      if (!s3Key) continue;
      const [, extension] = s3Key.split('.');
      if (
        !['png', 'jpg', 'jpeg', 'gif', 'heic'].includes(
          extension?.toLowerCase()
        )
      ) {
        continue;
      }
      images.push({
        smallUrl: `${endpoint}&key=${encodeURIComponent(s3Key)}&size=small`,
        largeUrl: `${endpoint}&key=${encodeURIComponent(s3Key)}&size=large`,
        originalUrl: `${file.url}&token=${accessToken}`,
        filename: file.name || 'Unnamed Image',
        created: file.created
      });
    }
    return images.sort((a, b) => b.created.getTime() - a.created.getTime());
  }, [accessToken, studyParticipantFiles]);
  return (
    <StyledCardView
      activeOpacity={0.7}
      onPress={() => {
        setOpenImageGallery(true);
      }}
    >
      <StyledH5 color='dark'>
        {title} {!loadingStudyParticipantFiles ? `(${images.length})` : ''}
      </StyledH5>
      {loadingStudyParticipantFiles && (
        <>
          <Spacer size={2} />
          <ActivityIndicatorScreen
            backgroundColor='transparent'
            color='white'
          />
          <Spacer size={4} />
        </>
      )}
      {/* <StyledImageStackView>
        {images?.map((image, index) => (
          <StyledTouchableOpacity
            key={index}
            style={{
              top: (images.length - index) * 4,
              right: (images.length - index) * -8
            }}
          >
            <StyledImage source={{ uri: image.smallUrl }} resizeMode='cover' />
          </StyledTouchableOpacity>
        ))}
      </StyledImageStackView> */}
      <StyledAcessoryContainerView>
        <Feather name='chevron-right' size={24} />
      </StyledAcessoryContainerView>

      {openImageGallery && (
        <ImageGalleryModal
          title={title}
          images={images}
          onRequestClose={() => setOpenImageGallery(false)}
        />
      )}
    </StyledCardView>
  );
};

export const ImageGalleryCardPlugin: ScreenComponentPlugin<ImageGalleryCardProps> =
  {
    type: 'ImageGalleryCard',
    name: 'Image Gallery Card',
    categories: ['study-progress'],
    component: ImageGalleryCard
  };
